import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ApiService } from '../../../../../core/services/api.service';
import { MockPrestampTaiwanService } from '../../../../../core/services/api/parking/mock-prestamp-taiwan.service';
import {
  PrestampDetailRow,
  PrestampRequestForm,
  PrestampRequestRow,
  PrestampStatus,
  PrestampSyncStatus,
} from '../../../../../core/models/parking/prestamp-taiwan.model';
import { ParkingSiteEvList } from '../../../../../core/models/ev.model';
import { ModalAdminActivityHistoryComponent } from '../../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-prestamp-taiwan',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './prestamp-taiwan.component.html',
  styleUrl: './prestamp-taiwan.component.css',
  providers: [DatePipe],
})
export class PrestampTaiwanComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly mock = inject(MockPrestampTaiwanService);
  private readonly apiLegacy = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  rows: PrestampRequestRow[] = [];
  siteEvOptions: ParkingSiteEvList[] = [];

  filterStatus: 'all' | PrestampStatus = 'all';
  filterSync: 'all' | PrestampSyncStatus = 'all';
  searchText = '';

  isLoading = false;
  isSaving = false;
  isModalOpen = false;
  modalMode: 'create' | 'edit' | 'view' = 'create';
  selected: PrestampRequestRow | null = null;

  form: PrestampRequestForm = this.emptyForm();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  userId = 0;

  constructor() {
    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;
      untracked(() => this.loadTable());
    });
  }

  ngOnInit(): void {
    if (!this.userId) {
      this.router.navigate(['/login']);
      return;
    }
    this.loadSiteEvOptions();
    if (this.siteState.selectedSite()?.id) {
      this.loadTable();
    }
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canEdit(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  statusLabel(s: PrestampStatus): string {
    return this.mock.statusLabel(s);
  }

  syncLabel(s: PrestampSyncStatus): string {
    return this.mock.syncLabel(s);
  }

  shortUuid(uuid?: string | null): string {
    if (!uuid) return '—';
    return uuid.length > 12 ? `${uuid.slice(0, 8)}…` : uuid;
  }

  setFilterStatus(status: 'all' | PrestampStatus): void {
    this.filterStatus = status;
    this.loadTable();
  }

  setFilterSync(status: 'all' | PrestampSyncStatus): void {
    this.filterSync = status;
    this.loadTable();
  }

  onSearch(): void {
    this.loadTable();
  }

  onRefresh(): void {
    this.loadTable();
  }

  openCreate(): void {
    this.modalMode = 'create';
    this.selected = null;
    this.form = this.emptyForm();
    this.form.siteId = this.selectedSiteId;
    this.isModalOpen = true;
  }

  openView(row: PrestampRequestRow): void {
    this.mock.getById(row.id).subscribe((detail) => {
      this.selected = detail;
      this.modalMode = 'view';
      this.isModalOpen = true;
    });
  }

  openEdit(row: PrestampRequestRow): void {
    this.mock.getById(row.id).subscribe((detail) => {
      if (!detail) return;
      this.selected = detail;
      this.modalMode = 'edit';
      this.form = {
        siteId: detail.siteId,
        ruleUuid: detail.ruleUuid,
        quantity: detail.quantity,
        stampLimit: detail.stampLimit,
        requestRemark: detail.requestRemark ?? '',
        details: (detail.details ?? []).map((d: PrestampDetailRow) => ({
          licensePlate: d.licensePlate,
          stampDate: d.stampDate,
        })),
      };
      if (this.form.details.length === 0) {
        this.addDetailRow();
      }
      this.isModalOpen = true;
    });
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.selected = null;
  }

  addDetailRow(): void {
    this.form.details.push({ licensePlate: '', stampDate: '' });
  }

  removeDetailRow(index: number): void {
    if (this.form.details.length <= 1) return;
    this.form.details.splice(index, 1);
  }

  copyUuid(): void {
    const uuid = this.selected?.taiwanScheduleUuid;
    if (!uuid) return;
    navigator.clipboard.writeText(uuid).then(
      () => this.toast.success('คัดลอก uuid แล้ว'),
      () => this.toast.error('คัดลอกไม่สำเร็จ')
    );
  }

  save(submit: boolean): void {
    const err = this.validateForm(submit);
    if (err) {
      this.toast.error(err);
      return;
    }

    this.isSaving = true;

    if (this.modalMode === 'create') {
      const site = this.siteState.selectedSite();
      const lotId =
        this.siteEvOptions.find((s) => s.id === this.form.siteId)?.evName?.trim() ||
        (site as { siteEv?: string } | null)?.siteEv ||
        'PSS_MOCK';
      const siteName = site?.name || 'Site';
      this.mock.create(this.form, siteName, lotId, submit).subscribe({
        next: () => {
          this.isSaving = false;
          this.toast.success(submit ? 'สร้างและส่ง (mock) สำเร็จ' : 'บันทึกร่าง (mock) สำเร็จ');
          this.closeModal();
          this.loadTable();
        },
        error: () => {
          this.isSaving = false;
          this.toast.error('บันทึกไม่สำเร็จ');
        },
      });
      return;
    }

    if (this.modalMode === 'edit' && this.selected) {
      const op =
        this.selected.status === 'draft'
          ? this.mock.updateDraft(this.selected.id, this.form)
          : this.mock.updateApprovedPairs(this.selected.id, this.form);
      op.subscribe({
        next: (ok) => {
          this.isSaving = false;
          if (!ok) {
            this.toast.error('แก้ไขไม่ได้ในสถานะนี้');
            return;
          }
          this.toast.success('อัปเดต (mock) สำเร็จ');
          this.closeModal();
          this.loadTable();
        },
        error: () => {
          this.isSaving = false;
          this.toast.error('อัปเดตไม่สำเร็จ');
        },
      });
    }
  }

  submitSelected(): void {
    if (!this.selected || !this.canEdit) return;
    this.isSaving = true;
    this.mock.submit(this.selected.id).subscribe({
      next: (ok) => {
        this.isSaving = false;
        if (!ok) {
          this.toast.error('ส่งไม่ได้');
          return;
        }
        this.toast.success('ส่งอนุมัติ + Taiwan (mock) สำเร็จ');
        this.closeModal();
        this.loadTable();
      },
      error: () => {
        this.isSaving = false;
        this.toast.error('ส่งไม่สำเร็จ');
      },
    });
  }

  cancelSelected(): void {
    if (!this.selected || !this.canDelete) return;
    if (!confirm('ยืนยันยกเลิกคำขอนี้?')) return;
    this.isSaving = true;
    this.mock.cancel(this.selected.id).subscribe({
      next: (ok) => {
        this.isSaving = false;
        if (!ok) {
          this.toast.error('ยกเลิกไม่ได้');
          return;
        }
        this.toast.success('ยกเลิก (mock) สำเร็จ');
        this.closeModal();
        this.loadTable();
      },
      error: () => {
        this.isSaving = false;
        this.toast.error('ยกเลิกไม่สำเร็จ');
      },
    });
  }

  retrySelected(): void {
    if (!this.selected || !this.canEdit) return;
    this.isSaving = true;
    this.mock.retry(this.selected.id).subscribe({
      next: (ok) => {
        this.isSaving = false;
        if (!ok) {
          this.toast.error('Retry ไม่ได้ (อาจมี uuid แล้ว)');
          return;
        }
        this.toast.success('Retry Taiwan (mock) สำเร็จ');
        this.closeModal();
        this.loadTable();
      },
      error: () => {
        this.isSaving = false;
        this.toast.error('Retry ไม่สำเร็จ');
      },
    });
  }

  trackById(_: number, row: PrestampRequestRow): number {
    return row.id;
  }

  private emptyForm(): PrestampRequestForm {
    return {
      siteId: 0,
      ruleUuid: '',
      quantity: 1,
      stampLimit: 1,
      requestRemark: '',
      details: [{ licensePlate: '', stampDate: '' }],
    };
  }

  private validateForm(forSubmit: boolean): string | null {
    if (!this.form.siteId) return 'กรุณาเลือกลาน';
    if (!this.form.ruleUuid.trim() || !/^[0-9a-fA-F-]{36}$/.test(this.form.ruleUuid.trim())) {
      return 'rule_uuid ต้องเป็น UUID';
    }
    if (this.form.quantity <= 0) return 'quantity ต้องมากกว่า 0';
    if (this.form.stampLimit <= 0) return 'limit ต้องมากกว่า 0';
    if (!this.form.details.length) return 'ต้องมีคู่ทะเบียน/วันอย่างน้อย 1 คู่';
    const seen = new Set<string>();
    for (const d of this.form.details) {
      const plate = d.licensePlate.trim();
      const date = d.stampDate.trim();
      if (!plate) return 'ทะเบียนว่างไม่ได้';
      if (!date) return 'วันที่ว่างไม่ได้';
      const key = `${plate}|${date}`;
      if (seen.has(key)) return `คู่ซ้ำ: ${plate} ${date}`;
      seen.add(key);
    }
    if (forSubmit && this.modalMode === 'edit' && this.selected?.status !== 'draft') {
      return null;
    }
    return null;
  }

  private loadTable(): void {
    this.isLoading = true;
    this.mock
      .list({
        siteId: this.selectedSiteId || undefined,
        status: this.filterStatus,
        syncStatus: this.filterSync,
        searchText: this.searchText,
      })
      .subscribe({
        next: (rows) => {
          this.rows = rows;
          this.isLoading = false;
        },
        error: () => {
          this.toast.error('โหลดรายการไม่สำเร็จ');
          this.isLoading = false;
        },
      });
  }

  private loadSiteEvOptions(): void {
    this.apiLegacy.getParkingSiteEvList().subscribe({
      next: (list) => {
        this.siteEvOptions = list ?? [];
      },
      error: () => {
        this.siteEvOptions = [];
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return (this.selected?.id ?? 0) > 0 && (this.modalMode === 'edit' || this.modalMode === 'view');
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
